import {Geometry} from "geojson";
import {
    CacheKey,
    getServiceCache,
    isCacheHitResponse,
    isJobIdResponse,
    ServiceCache
} from "@esignal/polygon-service-api";


class PolygonProviderJob {
    private readonly collected: Set<string> = new Set<string>();
    private readonly waitings: Set<string> = new Set<string>();
    private readonly errors: Set<string> = new Set<string>();

    private readonly responseCallback: (job: PolygonProviderJob) => void;

    constructor(collected: Set<string>, waitings: Set<string>, responseCallback?: (job: PolygonProviderJob) => void) {
        this.waitings = waitings;
        this.collected = collected;
        this.responseCallback = responseCallback;
    }

    isDone = (): boolean => this.waitings.size === 0;

    hasErrors = (): boolean => this.errors.size > 0;

    getErrorCount = () => this.errors.size;

    getWaitingCount = () => this.waitings.size;

    getCollectedCount = () => this.collected.size;

    callback = () => {
        if (this.responseCallback) {
            this.responseCallback(this);
        }
    }

    onError = (key: string): void => {
        this.errors.add(key);
        this.callback();
    }

    onSuccess = (key: string): void => {
        this.waitings.delete(key);
        this.collected.add(key);
        if (this.responseCallback) {
            this.responseCallback(this);
        }
    }
}


export class PolygonProvider {

    private static _instance: PolygonProvider;

    static getInstance = (): PolygonProvider => {
        if (!PolygonProvider._instance) {
            PolygonProvider._instance = new PolygonProvider();
        }
        return PolygonProvider._instance;
    }


    private serviceCache: ServiceCache = getServiceCache();
    private jobs: PolygonProviderJob[] = [];

    private createCacheKey = (country, state, county): CacheKey => {
        let key: CacheKey | undefined = undefined;

        if (country) {
            key = [country];
            if (state) {
                key.push(state);
                if (county) {
                    key.push(county);
                }
            }
        }
        return key;
    }

    private createMapKey = (country, state, county) => {
        const cacheKey = this.createCacheKey(country, state, county);
        // key = country | country_state | country_state_county
        return cacheKey.reduce((tempKey, curr, idx) => idx === 0 ? curr : tempKey + "_" + curr);
    }

    startJobIfNeeded = (
        entries: [string, string, string][],
        responseCallback: (job: PolygonProviderJob) => void
    ): void => {
        if (this.serviceCache) {
            const collected: Set<string> = new Set<string>();
            const waiting: Set<string> = new Set<string>();

            for (const [country, state, county] of entries) {
                const cacheKey = this.createCacheKey(country, state, county);
                let mapKey = this.createMapKey(country, state, county);
                const entry = this.serviceCache.getEntry(cacheKey);
                if (entry && isCacheHitResponse(entry)) {
                    collected.add(mapKey);
                } else if (entry && isJobIdResponse(entry)) {
                    waiting.add(mapKey);
                    this.serviceCache.addDataReadyListener(entry.jobId, {
                        onDataReady: (jobId, batchNumber, complete, hasErrors) => {
                            this.jobs.forEach(job => {
                                if (!job.isDone()) {
                                    if (hasErrors) {
                                        job.onError(mapKey);
                                    } else {
                                        job.onSuccess(mapKey);
                                    }
                                }
                            });
                            // remove all done jobs
                            this.jobs = this.jobs.filter(job => !job.isDone());
                        },
                    });
                }
            }

            if (waiting.size > 0) {
                let providerJob = new PolygonProviderJob(collected, waiting, responseCallback);
                this.jobs.push(providerJob);
                // do immediately callback if exists
                providerJob.callback();
            }
        }
    }

    getGeometry = (country: string, state: string, county: string): Geometry => {
        const cacheKey = this.createCacheKey(country, state, county);
        const entry = this.serviceCache.getEntry(cacheKey);
        return entry && isCacheHitResponse(entry) ? entry?.value?.geometry : null;
    }

    getPercentDone = (): number => {
        let waitingCount = 0;
        let collectedCount = 0;
        let errorCount = 0;
        for (const job of this.jobs) {
            waitingCount += job.getWaitingCount();
            collectedCount += job.getCollectedCount();
            errorCount += job.getErrorCount();
        }
        const total = waitingCount + collectedCount + errorCount;

        return total ? (collectedCount + errorCount) / total : 1;
    }

}